import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useSocieta } from '../../../context/SocietaContext';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { API_DOCS } from './NominativoNuovoPage.api';
import './NominativoNuovoPage.css';

// ── Interfaces ────────────────────────────────────────────────────────────
interface FormData {
  // Step 1
  partitaIva: string;
  tipoSoggetto: 'AZIENDA' | 'PRIVATO' | '';

  // Step 2
  ragioneSociale: string;
  nome: string;
  cognome: string;
  codiceFiscale: string;
  codiceSDI: string;
  pec: string;
  telefono: string;
  emailPrincipale: string;
  sito: string;
  via: string;
  cap: string;
  citta: string;
  provincia: string;
  paese: string;
  note: string;

  // Step 3
  isCliente: boolean;
  isFornitore: boolean;
  categoriaCliente: string;
  categoriaFornitore: string;

  // Step 4
  codice: string;
  accountId: string;
  insegnaClienteId: string;
  agenteNominativoId: string;
  noteCliente: string;
  iban: string;
  banca: string;
  filialeBanca: string;
  noteFornitore: string;
}

interface Utente {
  id: number;
  nome: string;
}

interface Insegna {
  id: number;
  nome: string;
}

interface Agente {
  id: number;
  ragioneSociale?: string;
  nome?: string;
  cognome?: string;
}

// ── Wizard steps config ───────────────────────────────────────────────────
const WIZARD_STEPS = [
  { n: 1, label: 'Identificazione' },
  { n: 2, label: 'Dati anagrafici' },
  { n: 3, label: 'Ruolo e categoria' },
  { n: 4, label: 'Specializzazione' },
] as const;

// ── Helper: selectable card className ─────────────────────────────────────
function selectableCardClassName(selected: boolean): string {
  const base = 'bop-nominativo-nuovo__selectable-card';
  return selected ? `${base} bop-nominativo-nuovo__selectable-card--selected` : base;
}

// ── Componente principale ─────────────────────────────────────────────────
export default function NominativoNuovoPage() {
  const navigate = useNavigate();
  const { societaAttiva, listaSocieta, loading: loadingSocieta } = useSocieta();

  const queryParams = new URLSearchParams(window.location.search);
  const querySocietaId = queryParams.get('societaId') || '';
  const societaId = querySocietaId || (societaAttiva ? String(societaAttiva.id) : '');
  const societaNome = listaSocieta.find(s => String(s.id) === societaId)?.nome || '';

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  // Lookup OpenApi (Step 1 → Step 2)
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupBanner, setLookupBanner] = useState<{
    tipo: 'success' | 'info' | 'warning';
    testo: string;
  } | null>(null);

  // Liste per select
  const [utenti, setUtenti] = useState<Utente[]>([]);
  const [insegne, setInsegne] = useState<Insegna[]>([]);
  const [agenti, setAgenti] = useState<Agente[]>([]);

  const [formData, setFormData] = useState<FormData>({
    partitaIva: '',
    tipoSoggetto: '',
    ragioneSociale: '',
    nome: '',
    cognome: '',
    codiceFiscale: '',
    codiceSDI: '',
    pec: '',
    telefono: '',
    emailPrincipale: '',
    sito: '',
    via: '',
    cap: '',
    citta: '',
    provincia: '',
    paese: 'Italia',
    note: '',
    isCliente: false,
    isFornitore: false,
    categoriaCliente: '',
    categoriaFornitore: '',
    codice: '',
    accountId: '',
    insegnaClienteId: '',
    agenteNominativoId: '',
    noteCliente: '',
    iban: '',
    banca: '',
    filialeBanca: '',
    noteFornitore: '',
  });

  // ── Dirty-check ──────────────────────────────────────────────────────────
  const [initialSnapshot, setInitialSnapshot] = useState(() => JSON.stringify(formData));
  const isDirty = JSON.stringify(formData) !== initialSnapshot;
  useDirtyForm(isDirty, 'Nuovo nominativo');

  // Carica dati iniziali quando arriva a step 4
  useEffect(() => {
    if (currentStep === 4 && societaId) {
      loadStep4Data();
    }
  }, [currentStep, societaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStep4Data = async () => {
    if (!societaId) return;
    try {
      // @api GET /api/utenti/selezionabili?societaId=N -> {"success":"true","data":[{"id":"number","nome":"string"}]}
      const utentiResp = await apiFetch<{ success: boolean; data: Utente[] }>(`/api/utenti/selezionabili?societaId=${societaId}`);
      setUtenti(utentiResp.data || []);

      // @api GET /api/anagrafiche/insegne?societaId=N -> [{"id":"number","nome":"string"}]
      const insegneResp = await apiFetch<Insegna[]>(`/api/anagrafiche/insegne?societaId=${societaId}`);
      setInsegne(insegneResp);

      // @api GET /api/nominativi?societaId=N&ruolo=FORNITORE&categoriaFornitore=AGENTE -> {"data":[...]}
      const params = new URLSearchParams();
      params.append('societaId', societaId);
      params.append('ruolo', 'FORNITORE');
      params.append('categoriaFornitore', 'AGENTE');
      const agentiResp = await apiFetch<{ data: Agente[] }>(`/api/nominativi?${params}`);
      setAgenti(agentiResp.data || []);

      // @api GET /api/nominativi/prossimo-codice?societaId=N -> {"codice":"string"}
      const codiceResp = await apiFetch<{ codice: string }>(`/api/nominativi/prossimo-codice?societaId=${societaId}`);
      setFormData(prev => ({ ...prev, codice: codiceResp.codice }));
    } catch (err: any) {
      console.error('[nominativoNuovo][loadStep4Data]', err.message, err);
      setApiError(err.message || 'Errore nel caricamento dei dati');
    }
  };

  const handleAnnulla = () => {
    if (isDirty) {
      if (!window.confirm('Sei sicuro di voler annullare? I dati inseriti andranno persi.')) return;
    }
    navigate(-1);
  };

  const validateStep1 = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.partitaIva && !formData.tipoSoggetto) {
      newErrors.step1 = 'Inserisci una Partita IVA oppure seleziona il tipo di soggetto';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (formData.tipoSoggetto === 'AZIENDA' && !formData.ragioneSociale.trim()) {
      newErrors.ragioneSociale = 'Ragione sociale obbligatoria per aziende';
    }
    if (formData.tipoSoggetto === 'PRIVATO') {
      if (!formData.nome.trim()) newErrors.nome = 'Nome obbligatorio';
      if (!formData.cognome.trim()) newErrors.cognome = 'Cognome obbligatorio';
    }
    if (!formData.citta.trim()) newErrors.citta = 'Città obbligatoria';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.isCliente && !formData.isFornitore) {
      newErrors.ruolo = 'Seleziona almeno un ruolo (Cliente o Fornitore)';
    }
    if (formData.isCliente && !formData.categoriaCliente) {
      newErrors.categoriaCliente = 'Categoria cliente obbligatoria';
    }
    if (formData.isFornitore && !formData.categoriaFornitore) {
      newErrors.categoriaFornitore = 'Categoria fornitore obbligatoria';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLookupPiva = async () => {
    if (!formData.partitaIva.trim()) return;
    if (!societaId) {
      setApiError('Nessuna società selezionata');
      return;
    }

    const codice = formData.partitaIva.trim().toUpperCase();
    const isPiva = /^\d{11}$/.test(codice);
    const isCf   = /^[A-Z0-9]{16}$/.test(codice);
    if (!isPiva && !isCf) {
      setLookupBanner({
        tipo: 'warning',
        testo: 'Inserisci una P.IVA italiana (11 cifre) o un Codice Fiscale (16 caratteri).',
      });
      return;
    }

    setLookupLoading(true);
    setLookupBanner(null);
    setApiError('');

    try {
      // @api GET /api/openapi/lookup-azienda?societaId=N&codice=XXX -> {"success":true,"trovato":true,"data":{...},"fontecache":false}
      const resp = await apiFetch<{
        success: boolean;
        trovato: boolean;
        data: {
          ragioneSociale: string;
          partitaIva: string;
          codiceFiscale: string;
          codiceSDI: string | null;
          indirizzo: { via: string; cap: string; citta: string; provincia: string; paese: string };
        } | null;
        fontecache: boolean;
      }>(`/api/openapi/lookup-azienda?societaId=${societaId}&codice=${encodeURIComponent(codice)}`);

      if (resp.trovato && resp.data) {
        const d = resp.data;
        setFormData((prev) => ({
          ...prev,
          tipoSoggetto: 'AZIENDA',
          ragioneSociale: d.ragioneSociale || prev.ragioneSociale,
          partitaIva:     d.partitaIva     || prev.partitaIva,
          codiceFiscale:  d.codiceFiscale  || prev.codiceFiscale,
          codiceSDI:      d.codiceSDI      || prev.codiceSDI,
          via:            d.indirizzo?.via       || prev.via,
          cap:            d.indirizzo?.cap       || prev.cap,
          citta:          d.indirizzo?.citta     || prev.citta,
          provincia:      d.indirizzo?.provincia || prev.provincia,
          paese:          d.indirizzo?.paese     || prev.paese,
        }));
        setLookupBanner({
          tipo: 'success',
          testo: `Dati trovati per ${d.ragioneSociale}${resp.fontecache ? ' (cache)' : ''}. Verifica e modifica nel passo successivo.`,
        });
        setCurrentStep(2);
      } else {
        setFormData((prev) => ({ ...prev, tipoSoggetto: 'AZIENDA' }));
        setLookupBanner({
          tipo: 'info',
          testo: 'Nessuna azienda trovata con questo codice. Compila manualmente i dati al passo successivo.',
        });
        setCurrentStep(2);
      }
    } catch (err: any) {
      const msg = err?.message || 'Errore nel lookup';
      const lower = msg.toLowerCase();
      if (lower.includes('credito') || lower.includes('credit')) {
        setLookupBanner({ tipo: 'warning', testo: 'Credito OpenApi insufficiente. Continua manualmente cliccando una delle due card sotto.' });
      } else if (lower.includes('non attiva') || lower.includes('non configurat')) {
        setLookupBanner({ tipo: 'warning', testo: 'Integrazione OpenApi non configurata o non attiva per questa società. Continua manualmente.' });
      } else {
        setLookupBanner({ tipo: 'warning', testo: `${msg}. Continua manualmente.` });
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const handleNext = () => {
    setApiError('');
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
      setApiError('');
    }
  };

  const handleSave = async () => {
    if (!societaId) {
      setApiError('Nessuna società selezionata');
      return;
    }

    setLoading(true);
    setApiError('');

    try {
      // @api POST /api/nominativi {"societaId":"number","tipoSoggetto":"string",...} -> {"id":"number"}
      const createBody: any = {
        societaId,
        tipoSoggetto: formData.tipoSoggetto,
        codice: formData.codice,
        partitaIva: formData.partitaIva || undefined,
        codiceFiscale: formData.codiceFiscale || undefined,
        codiceSDI: formData.codiceSDI || undefined,
        pec: formData.pec || undefined,
        telefono: formData.telefono || undefined,
        emailPrincipale: formData.emailPrincipale || undefined,
        sito: formData.sito || undefined,
        note: formData.note || undefined,
      };

      if (formData.tipoSoggetto === 'AZIENDA') {
        createBody.ragioneSociale = formData.ragioneSociale;
      } else {
        createBody.nome = formData.nome;
        createBody.cognome = formData.cognome;
      }

      if (formData.via || formData.cap || formData.citta || formData.provincia || formData.paese) {
        createBody.indirizzoSedeLegale = {
          via: formData.via || undefined,
          cap: formData.cap || undefined,
          citta: formData.citta,
          provincia: formData.provincia || undefined,
          paese: formData.paese || 'Italia',
        };
      }

      const createdNominativo = await apiFetch<{ id: number }>('/api/nominativi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createBody),
      });

      const nominativoId = createdNominativo.id;

      if (formData.isCliente) {
        // @api POST /api/nominativi/:id/abilita-cliente {"categoriaCliente":"string",...} -> {"success":true}
        await apiFetch(`/api/nominativi/${nominativoId}/abilita-cliente`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            categoriaCliente: formData.categoriaCliente,
            accountId: formData.accountId ? parseInt(formData.accountId) : undefined,
            insegnaClienteId: formData.insegnaClienteId ? parseInt(formData.insegnaClienteId) : undefined,
            agenteNominativoId: formData.agenteNominativoId ? parseInt(formData.agenteNominativoId) : undefined,
            note: formData.noteCliente || undefined,
          }),
        });
      }

      if (formData.isFornitore) {
        // @api POST /api/nominativi/:id/abilita-fornitore {"categoriaFornitore":"string",...} -> {"success":true}
        await apiFetch(`/api/nominativi/${nominativoId}/abilita-fornitore`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            categoriaFornitore: formData.categoriaFornitore,
            iban: formData.iban || undefined,
            banca: formData.banca || undefined,
            filialeBanca: formData.filialeBanca || undefined,
            note: formData.noteFornitore || undefined,
          }),
        });
      }

      setInitialSnapshot(JSON.stringify(formData));
      navigate(`/anagrafiche/nominativi/${nominativoId}`);
    } catch (err: any) {
      console.error('[nominativoNuovo][handleSave]', err.message, err);
      if (err.status === 409) {
        setApiError('Codice già utilizzato per questa società. Modifica il codice e riprova.');
        setErrors({ codice: 'Codice duplicato' });
      } else {
        setApiError(err.message || 'Errore durante il salvataggio del nominativo');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loadingSocieta) {
    return (
      <Layout>
        <p className="bop-text-muted">Caricamento...</p>
      </Layout>
    );
  }

  if (!societaId) {
    return (
      <Layout>
        <div className="bop-alert bop-alert--danger">
          Nessuna società selezionata. Seleziona una società dalla barra in alto e riprova.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Nuovo nominativo</h1>
          {societaNome && <p className="bop-page-subtitle">{societaNome}</p>}
        </div>
        <button className="bop-btn-secondary" onClick={handleAnnulla}>
          Annulla
        </button>
      </div>

      {/* Wizard stepper */}
      <div className="bop-wizard__stepper">
        {WIZARD_STEPS.map((step, idx, arr) => (
          <React.Fragment key={step.n}>
            <div className="bop-wizard__step">
              <div className={`bop-wizard__step-circle ${
                currentStep > step.n ? 'bop-wizard__step-circle--done' :
                currentStep === step.n ? 'bop-wizard__step-circle--active' :
                'bop-wizard__step-circle--pending'
              }`}>
                {currentStep > step.n ? '✓' : step.n}
              </div>
              <span className={`bop-wizard__step-label ${
                currentStep === step.n ? 'bop-wizard__step-label--active' : 'bop-wizard__step-label--inactive'
              }`}>
                {step.label}
              </span>
            </div>
            {idx < arr.length - 1 && (
              <div className={`bop-wizard__step-line ${
                currentStep > step.n ? 'bop-wizard__step-line--done' : 'bop-wizard__step-line--pending'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {apiError && (
        <div className="bop-alert bop-alert--danger">{apiError}</div>
      )}

      <div className="nominativo-nuovo__content">
        {currentStep === 1 && (
          <Step1
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            onLookupPiva={handleLookupPiva}
            lookupLoading={lookupLoading}
            lookupBanner={lookupBanner}
          />
        )}
        {currentStep === 2 && <Step2 formData={formData} setFormData={setFormData} errors={errors} />}
        {currentStep === 3 && <Step3 formData={formData} setFormData={setFormData} errors={errors} />}
        {currentStep === 4 && (
          <Step4
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            utenti={utenti}
            insegne={insegne}
            agenti={agenti}
            societaNome={societaNome}
          />
        )}
      </div>

      <div className="nominativo-nuovo__actions">
        <span className="bop-text-muted">Step {currentStep} di 4</span>
        <div className="nominativo-nuovo__actions-right">
          {currentStep > 1 && (
            <button className="bop-btn-secondary" onClick={handleBack} disabled={loading}>
              Indietro
            </button>
          )}
          {currentStep < 4 ? (
            <button className="bop-btn-primary" onClick={handleNext}>
              Avanti
            </button>
          ) : (
            <button className="bop-btn-primary" onClick={handleSave} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva nominativo'}
            </button>
          )}
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ── Step 1: Identificazione ───────────────────────────────────────────────
function Step1({ formData, setFormData, errors, onLookupPiva, lookupLoading, lookupBanner }: any) {
  const [mode, setMode] = useState<'piva' | 'tipo' | ''>('');

  const handlePIvaMode = () => {
    setMode('piva');
    setFormData((prev: FormData) => ({ ...prev, tipoSoggetto: '' }));
  };

  const handleTipoMode = () => {
    setMode('tipo');
    setFormData((prev: FormData) => ({ ...prev, partitaIva: '' }));
  };

  const bannerClass = lookupBanner
    ? lookupBanner.tipo === 'warning'
      ? 'bop-nominativo-nuovo__banner-warning'
      : 'bop-nominativo-nuovo__banner-info'
    : '';

  return (
    <>
      <div className="bop-card">
        <div className="bop-card__header">
          <h2 className="bop-card__title">Hai la Partita IVA del soggetto?</h2>
        </div>
        <div className="bop-card__body">
          <p className="bop-nominativo-nuovo__subtitle">
            Inserisci la Partita IVA per una compilazione assistita dei dati anagrafici.
          </p>
          <input
            type="text"
            className="bop-nominativo-nuovo__input--large"
            placeholder="Es. 02341780962"
            value={formData.partitaIva}
            onChange={(e) => {
              setFormData((prev: FormData) => ({ ...prev, partitaIva: e.target.value }));
              handlePIvaMode();
            }}
          />
          {lookupBanner && (
            <div className={bannerClass}>{lookupBanner.testo}</div>
          )}
          {mode === 'piva' && formData.partitaIva && (
            <div className="bop-nominativo-nuovo__piva-action">
              <button
                className="bop-btn-primary"
                onClick={onLookupPiva}
                disabled={lookupLoading || !formData.partitaIva.trim()}
              >
                {lookupLoading ? 'Cercando...' : 'Cerca e continua'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bop-nominativo-nuovo__separator">
        <span className="bop-nominativo-nuovo__separator-text">oppure</span>
      </div>

      <div className="bop-card">
        <div className="bop-card__header">
          <h2 className="bop-card__title">Seleziona il tipo di soggetto senza P.IVA</h2>
        </div>
        <div className="bop-card__body">
          <div
            className={selectableCardClassName(formData.tipoSoggetto === 'AZIENDA')}
            onClick={() => {
              setFormData((prev: FormData) => ({ ...prev, tipoSoggetto: 'AZIENDA' }));
              handleTipoMode();
            }}
          >
            <div className="bop-nominativo-nuovo__selectable-card-title">Azienda</div>
            <div className="bop-nominativo-nuovo__selectable-card-desc">
              Soggetto giuridico con ragione sociale
            </div>
          </div>

          <div
            className={selectableCardClassName(formData.tipoSoggetto === 'PRIVATO')}
            onClick={() => {
              setFormData((prev: FormData) => ({ ...prev, tipoSoggetto: 'PRIVATO' }));
              handleTipoMode();
            }}
          >
            <div className="bop-nominativo-nuovo__selectable-card-title">Privato</div>
            <div className="bop-nominativo-nuovo__selectable-card-desc">
              Persona fisica con nome e cognome
            </div>
          </div>

          {errors.step1 && <div className="bop-nominativo-nuovo__error">{errors.step1}</div>}
        </div>
      </div>
    </>
  );
}

// ── Step 2: Dati anagrafici ───────────────────────────────────────────────
function Step2({ formData, setFormData, errors }: any) {
  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev: FormData) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Identificazione</h3>
        </div>
        <div className="bop-card__body">
          <div className="bop-form-row">
            {formData.tipoSoggetto === 'AZIENDA' ? (
              <div className="bop-form-group nominativo-nuovo__field--full">
                <label className="bop-label">Ragione sociale *</label>
                <input
                  type="text"
                  className="bop-input"
                  value={formData.ragioneSociale}
                  onChange={(e) => handleChange('ragioneSociale', e.target.value)}
                />
                {errors.ragioneSociale && <div className="bop-nominativo-nuovo__error">{errors.ragioneSociale}</div>}
              </div>
            ) : (
              <>
                <div className="bop-form-group">
                  <label className="bop-label">Nome *</label>
                  <input
                    type="text"
                    className="bop-input"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                  />
                  {errors.nome && <div className="bop-nominativo-nuovo__error">{errors.nome}</div>}
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cognome *</label>
                  <input
                    type="text"
                    className="bop-input"
                    value={formData.cognome}
                    onChange={(e) => handleChange('cognome', e.target.value)}
                  />
                  {errors.cognome && <div className="bop-nominativo-nuovo__error">{errors.cognome}</div>}
                </div>
              </>
            )}
            <div className="bop-form-group">
              <label className="bop-label">Partita IVA</label>
              <input
                type="text"
                className="bop-input"
                value={formData.partitaIva}
                onChange={(e) => handleChange('partitaIva', e.target.value)}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Codice fiscale</label>
              <input
                type="text"
                className="bop-input"
                value={formData.codiceFiscale}
                onChange={(e) => handleChange('codiceFiscale', e.target.value)}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Codice SDI</label>
              <input
                type="text"
                className="bop-input"
                value={formData.codiceSDI}
                onChange={(e) => handleChange('codiceSDI', e.target.value)}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">PEC</label>
              <input
                type="email"
                className="bop-input"
                value={formData.pec}
                onChange={(e) => handleChange('pec', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Recapiti</h3>
        </div>
        <div className="bop-card__body">
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Telefono</label>
              <input
                type="tel"
                className="bop-input"
                value={formData.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Email principale</label>
              <input
                type="email"
                className="bop-input"
                value={formData.emailPrincipale}
                onChange={(e) => handleChange('emailPrincipale', e.target.value)}
              />
            </div>
            <div className="bop-form-group nominativo-nuovo__field--full">
              <label className="bop-label">Sito web</label>
              <input
                type="url"
                className="bop-input"
                value={formData.sito}
                onChange={(e) => handleChange('sito', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Sede legale</h3>
        </div>
        <div className="bop-card__body">
          <div className="bop-form-row">
            <div className="bop-form-group nominativo-nuovo__field--full">
              <label className="bop-label">Via e numero civico</label>
              <input
                type="text"
                className="bop-input"
                value={formData.via}
                onChange={(e) => handleChange('via', e.target.value)}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">CAP</label>
              <input
                type="text"
                className="bop-input"
                value={formData.cap}
                onChange={(e) => handleChange('cap', e.target.value)}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Città *</label>
              <input
                type="text"
                className="bop-input"
                value={formData.citta}
                onChange={(e) => handleChange('citta', e.target.value)}
              />
              {errors.citta && <div className="bop-nominativo-nuovo__error">{errors.citta}</div>}
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Provincia (2 lettere)</label>
              <input
                type="text"
                className="bop-input"
                maxLength={2}
                value={formData.provincia}
                onChange={(e) => handleChange('provincia', e.target.value.toUpperCase())}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Paese</label>
              <input
                type="text"
                className="bop-input"
                value={formData.paese}
                onChange={(e) => handleChange('paese', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Note</h3>
        </div>
        <div className="bop-card__body">
          <textarea
            className="bop-nominativo-nuovo__textarea"
            value={formData.note}
            onChange={(e) => handleChange('note', e.target.value)}
            placeholder="Note interne visibili solo agli operatori..."
          />
        </div>
      </div>
    </>
  );
}

// ── Step 3: Ruolo e categoria ─────────────────────────────────────────────
function Step3({ formData, setFormData, errors }: any) {
  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev: FormData) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bop-card">
      <div className="bop-card__header">
        <h3 className="bop-card__title">Seleziona il ruolo del nominativo</h3>
      </div>
      <div className="bop-card__body">
        <p className="bop-nominativo-nuovo__subtitle">
          Puoi selezionare entrambi i ruoli se necessario.
        </p>

        {/* Card Cliente */}
        <div
          className={selectableCardClassName(formData.isCliente)}
          onClick={() => handleChange('isCliente', !formData.isCliente)}
        >
          <div className="bop-nominativo-nuovo__role-row">
            <input
              type="checkbox"
              checked={formData.isCliente}
              onChange={(e) => handleChange('isCliente', e.target.checked)}
              className="bop-nominativo-nuovo__role-checkbox"
            />
            <div className="bop-nominativo-nuovo__role-content">
              <div className="bop-nominativo-nuovo__selectable-card-title bop-nominativo-nuovo__selectable-card-title--mb">
                Cliente
              </div>
              <div className="bop-nominativo-nuovo__selectable-card-desc bop-nominativo-nuovo__selectable-card-desc--lh">
                Genera ciclo attivo: preventivi, ordini, fatture attive
              </div>
            </div>
          </div>
          {formData.isCliente && (
            <div className="bop-nominativo-nuovo__role-expand" onClick={(e) => e.stopPropagation()}>
              <div className="bop-form-group">
                <label className="bop-label">Categoria cliente *</label>
                <select
                  className="bop-select"
                  value={formData.categoriaCliente}
                  onChange={(e) => handleChange('categoriaCliente', e.target.value)}
                >
                  <option value="">Seleziona categoria</option>
                  <option value="CATENA_GDO">Catena GDO</option>
                  <option value="AZIENDA_B2B">Azienda B2B</option>
                  <option value="PRIVATO_B2C">Privato B2C</option>
                  <option value="AGENTE">Agente</option>
                </select>
                {errors.categoriaCliente && <div className="bop-nominativo-nuovo__error">{errors.categoriaCliente}</div>}
              </div>
              <div className="bop-nominativo-nuovo__banner-warning">
                La categoria guida i flussi del ciclo attivo e non è facilmente modificabile in seguito
              </div>
            </div>
          )}
        </div>

        {/* Card Fornitore */}
        <div
          className={selectableCardClassName(formData.isFornitore)}
          onClick={() => handleChange('isFornitore', !formData.isFornitore)}
        >
          <div className="bop-nominativo-nuovo__role-row">
            <input
              type="checkbox"
              checked={formData.isFornitore}
              onChange={(e) => handleChange('isFornitore', e.target.checked)}
              className="bop-nominativo-nuovo__role-checkbox"
            />
            <div className="bop-nominativo-nuovo__role-content">
              <div className="bop-nominativo-nuovo__selectable-card-title bop-nominativo-nuovo__selectable-card-title--mb">
                Fornitore
              </div>
              <div className="bop-nominativo-nuovo__selectable-card-desc bop-nominativo-nuovo__selectable-card-desc--lh">
                Genera ciclo passivo: ordini acquisto, fatture passive, scadenzario
              </div>
            </div>
          </div>
          {formData.isFornitore && (
            <div className="bop-nominativo-nuovo__role-expand" onClick={(e) => e.stopPropagation()}>
              <div className="bop-form-group">
                <label className="bop-label">Categoria fornitore *</label>
                <select
                  className="bop-select"
                  value={formData.categoriaFornitore}
                  onChange={(e) => handleChange('categoriaFornitore', e.target.value)}
                >
                  <option value="">Seleziona categoria</option>
                  <option value="FORNITORE_MERCE">Fornitore merce</option>
                  <option value="SUBAPPALTATORE">Subappaltatore</option>
                  <option value="FORNITORE_SERVIZI">Fornitore servizi</option>
                  <option value="AGENTE">Agente</option>
                </select>
                {errors.categoriaFornitore && <div className="bop-nominativo-nuovo__error">{errors.categoriaFornitore}</div>}
              </div>
              <div className="bop-nominativo-nuovo__banner-warning">
                La categoria guida i flussi del ciclo passivo e non è facilmente modificabile in seguito
              </div>
            </div>
          )}
        </div>

        {errors.ruolo && <div className="bop-nominativo-nuovo__error">{errors.ruolo}</div>}

        <div className="bop-nominativo-nuovo__banner-warning">
          Il ruolo e la categoria hanno effetti operativi importanti su tutto il software. Verifica attentamente prima di procedere.
        </div>
      </div>
    </div>
  );
}

// ── Step 4: Specializzazione e riepilogo ──────────────────────────────────
function Step4({ formData, setFormData, errors, utenti, insegne, agenti, societaNome }: any) {
  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev: FormData) => ({ ...prev, [field]: value }));
  };

  const getNomeCompleto = () => {
    if (formData.tipoSoggetto === 'AZIENDA') {
      return formData.ragioneSociale || '(non specificato)';
    }
    return `${formData.nome} ${formData.cognome}`.trim() || '(non specificato)';
  };

  return (
    <>
      {formData.isCliente && (
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">
              Dettagli cliente — {formData.categoriaCliente.replace(/_/g, ' ')}
            </h3>
          </div>
          <div className="bop-card__body">
            <div className="bop-form-group">
              <label className="bop-label">Account assegnato</label>
              <select
                className="bop-select"
                value={formData.accountId}
                onChange={(e) => handleChange('accountId', e.target.value)}
              >
                <option value="">Nessuno</option>
                {utenti.map((u: Utente) => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Insegna di appartenenza</label>
              <select
                className="bop-select"
                value={formData.insegnaClienteId}
                onChange={(e) => handleChange('insegnaClienteId', e.target.value)}
              >
                <option value="">Nessuna</option>
                {insegne.map((i: Insegna) => (
                  <option key={i.id} value={i.id}>{i.nome}</option>
                ))}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Agente di riferimento</label>
              <select
                className="bop-select"
                value={formData.agenteNominativoId}
                onChange={(e) => handleChange('agenteNominativoId', e.target.value)}
              >
                <option value="">Nessuno</option>
                {agenti.map((a: Agente) => (
                  <option key={a.id} value={a.id}>
                    {a.ragioneSociale || `${a.nome} ${a.cognome}`.trim()}
                  </option>
                ))}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Note cliente</label>
              <input
                type="text"
                className="bop-input"
                value={formData.noteCliente}
                onChange={(e) => handleChange('noteCliente', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {formData.isFornitore && (
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">
              Dettagli fornitore — {formData.categoriaFornitore.replace(/_/g, ' ')}
            </h3>
          </div>
          <div className="bop-card__body">
            <div className="bop-form-row">
              <div className="bop-form-group nominativo-nuovo__field--full">
                <label className="bop-label">IBAN</label>
                <input
                  type="text"
                  className="bop-input"
                  value={formData.iban}
                  onChange={(e) => handleChange('iban', e.target.value)}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Banca</label>
                <input
                  type="text"
                  className="bop-input"
                  value={formData.banca}
                  onChange={(e) => handleChange('banca', e.target.value)}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Filiale banca</label>
                <input
                  type="text"
                  className="bop-input"
                  value={formData.filialeBanca}
                  onChange={(e) => handleChange('filialeBanca', e.target.value)}
                />
              </div>
              <div className="bop-form-group nominativo-nuovo__field--full">
                <label className="bop-label">Note fornitore</label>
                <input
                  type="text"
                  className="bop-input"
                  value={formData.noteFornitore}
                  onChange={(e) => handleChange('noteFornitore', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Codice interno</h3>
        </div>
        <div className="bop-card__body">
          <div className="bop-form-group">
            <label className="bop-label">Codice nominativo</label>
            <input
              type="text"
              className="bop-nominativo-nuovo__input--mono bop-input"
              value={formData.codice}
              onChange={(e) => handleChange('codice', e.target.value)}
            />
            {errors.codice && <div className="bop-nominativo-nuovo__error">{errors.codice}</div>}
            <div className="bop-nominativo-nuovo__banner-info bop-nominativo-nuovo__banner-info--mt-sm">
              Generato automaticamente — modificabile prima del salvataggio
            </div>
          </div>
        </div>
      </div>

      <div className="bop-nominativo-nuovo__summary">
        <h3 className="bop-nominativo-nuovo__summary-title">Riepilogo</h3>
        <div className="bop-nominativo-nuovo__summary-body">
          <div><strong>Nome/Ragione sociale:</strong> {getNomeCompleto()}</div>
          <div><strong>Partita IVA:</strong> {formData.partitaIva || '(non inserita)'}</div>
          <div>
            <strong>Ruolo:</strong>{' '}
            {formData.isCliente && 'Cliente'}{formData.isCliente && formData.isFornitore && ' + '}
            {formData.isFornitore && 'Fornitore'}
          </div>
          <div>
            <strong>Categorie:</strong>{' '}
            {formData.isCliente && formData.categoriaCliente.replace(/_/g, ' ')}
            {formData.isCliente && formData.isFornitore && ' + '}
            {formData.isFornitore && formData.categoriaFornitore.replace(/_/g, ' ')}
          </div>
          <div><strong>Codice:</strong> {formData.codice}</div>
          <div><strong>Società:</strong> {societaNome}</div>
        </div>
      </div>
    </>
  );
}
